import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";
import { AdminPatientDetail } from "components/features/patient";
import { useAdminPatientDetail } from "hooks/patientDetail";
import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router";

const PatientDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { patientId = "" } = useParams<{ patientId: string }>();

  const { patient, hasRequestedSuccessfullyBefore, loading, error, refetch } =
    useAdminPatientDetail(patientId);

  const patientName = useMemo(() => {
    if (!patient) {
      return "Patient Detail";
    }
    return [patient.firstName, patient.middleName, patient.lastName]
      .filter(Boolean)
      .join(" ");
  }, [patient]);

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", href: EPath.HOME, order: 1 },
      { label: "Patient Management", href: EPath.PATIENT_MANAGEMENT, order: 2 },
    ],
    [],
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="textSecondary">
            Loading patient details...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert
          severity="error"
          sx={{ maxWidth: 480 }}
          action={
            <Typography
              component="button"
              onClick={() => refetch()}
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                background: "none",
                border: "none",
                color: "inherit",
              }}
            >
              Retry
            </Typography>
          }
        >
          {error}
        </Alert>
      );
    }

    if (!patient) {
      return (
        <Alert severity="info" sx={{ maxWidth: 480 }}>
          We could not load this patient. Please verify the patient ID or try
          again later.
        </Alert>
      );
    }

    return (
      <AdminPatientDetail
        patient={patient}
        hasRequestedSuccessfullyBefore={hasRequestedSuccessfullyBefore}
      />
    );
  };

  return (
    <DashboardLayout
      title="Patient Detail"
      breadcrumbs={breadcrumbs}
      lastBreadcrumb={patientName}
      onBack={() => navigate(EPath.PATIENT_MANAGEMENT)}
      onRefresh={refetch}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          padding: 2,
        }}
      >
        {renderBody()}
      </Box>
    </DashboardLayout>
  );
};

export default PatientDetailPage;
